import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useAuth } from '@bymason-private/auth-provider';
import { makeStyles } from '@bymason-private/controller-v1-theme-provider';
import { useRouter } from '@bymason-private/route-provider';
import { Button } from '@bymason-private/legacy-pattern-components';
import {
  CreateProjectModal,
  useToast,
  retreiveBuildWithCli,
} from '@bymason-private/controller-shared-widgets';
import { CreateProjectResponse, useGetHardware, useListAllProjects } from '@bymason/controller-api-hooks';
import { formatLongDateTimeString, removeQueryParamFromCurrentUrl } from '@bymason/utils';

import Layout from '../../common/layout';
import InfoCard from '../../common/info-card';
import { routes } from '../../router';
import NoProjects from './no-projects';
import { useCheckFeatureFlag } from '../../global-state';

const useStyles = makeStyles(() => ({
  projectsSection: {
    width: '100%',
    padding: `8px 0 16px 0`,
  },
  projectsSectionHasData: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    columnGap: 20,
    rowGap: 20,
  },

  '@media (max-width: 1170px)': {
    projectsSectionHasData: {
      gridTemplateColumns: 'repeat(1, 1fr)',
    },
  },
}));

const UNSUPPORTED_DEVICE_FAMILIES = ['D03'];

export default function ProjectsView() {
  const { authToken = '', customerId } = useAuth();
  const [setToastMessage, setToastSeverity] = useToast();
  const { navigate } = useRouter();
  const classes = useStyles();
  const [getHardware, hardware, hardwareError, hardwareBusy] = useGetHardware(authToken);
  const [getProjects, projects, projectsError, projectsBusy] = useListAllProjects(authToken);
  const [isBuildEnabled] = useCheckFeatureFlag('controller_build');

  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);

  const handleCardClick = (projectId: string) => (evt: React.MouseEvent<HTMLAnchorElement>) => {
    evt.preventDefault();
    navigate(routes.Project, { projectId });
  };

  const handleCreateProjectModalOpen = () => {
    setShowCreateProjectModal(true);
  };

  const handleCreateProjectModalClose = () => {
    setShowCreateProjectModal(false);
  };

  const handleCreateProjectSuccess = useCallback(
    (newProject: CreateProjectResponse) => {
      if (newProject) {
        setShowCreateProjectModal(false);
        setToastSeverity('success');
        setToastMessage(`Successfully created project "${newProject.name}"`);
        if (retreiveBuildWithCli()) {
          navigate(routes.Project, { projectId: newProject.id });
        } else {
          navigate(routes.ProjectBuildStepper, { projectId: newProject.id, stepId: 'apps' });
        }
      }
    },
    [navigate, setToastSeverity, setToastMessage]
  );

  const handleCreateProjectError = useCallback(
    (errorMessage: string) => {
      if (errorMessage) {
        setToastSeverity('error');
        setToastMessage(errorMessage);
      }
    },
    [setToastSeverity, setToastMessage]
  );

  const supportedDevicesList = useMemo(
    () => hardware && hardware.devices.filter((d) => !UNSUPPORTED_DEVICE_FAMILIES.includes(d.familyName)),
    [hardware]
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('create') === 'true') {
      setShowCreateProjectModal(true);
      removeQueryParamFromCurrentUrl('create');
    }
  }, []);

  useEffect(() => {
    getHardware({});
  }, [getHardware]);

  useEffect(() => {
    if (customerId) {
      getProjects({ customerId });
    }
  }, [customerId, getProjects]);

  useEffect(() => {
    const errorMessage = projectsError || hardwareError || '';

    if (errorMessage) {
      setToastSeverity('error');
      setToastMessage(errorMessage);
    }
  }, [hardwareError, projectsError, setToastMessage, setToastSeverity]);

  return (
    <Layout
      busy={projectsBusy || hardwareBusy}
      headerText='Projects'
      headerRightAdornment={
        <Button variant='outlined' onClick={handleCreateProjectModalOpen}>
          Create project
        </Button>
      }
      stickyHeader
    >
      <section
        className={clsx({
          [classes.projectsSection]: true,
          [classes.projectsSectionHasData]: projects && projects.length > 0 ? true : false,
        })}
      >
        {projects && projects.length > 0
          ? projects
              .sort((proj1, proj2) => proj1.name.localeCompare(proj2.name))
              .map((info, idx) => (
                <InfoCard
                  key={info.name + idx}
                  title={info.name}
                  description={info.description || ''}
                  detailItems={[
                    {
                      type: 'devices',
                      value: info.deviceFamily || 'No devices',
                    },
                    {
                      type: 'api',
                      value: String(info.apiLevel),
                    },
                    {
                      type: 'createdAt',
                      value: formatLongDateTimeString(info.createdAt),
                    },
                  ]}
                  href={`${window.location.pathname}/${info.name}`}
                  onClick={handleCardClick(info.id)}
                />
              ))
          : !projectsBusy &&
            !projectsError && <NoProjects onCreateProjectClick={handleCreateProjectModalOpen} />}
      </section>
      <CreateProjectModal
        open={showCreateProjectModal}
        onClose={handleCreateProjectModalClose}
        onSuccess={handleCreateProjectSuccess}
        onError={handleCreateProjectError}
        controllerBuildEnabled={isBuildEnabled}
        deviceList={supportedDevicesList ?? []}
        existingProjectNames={projects?.map((proj) => proj.name)}
      />
    </Layout>
  );
}
